/**
 * Ce que le cabinet ouvre à son client, dossier par dossier.
 *
 * C'est le cabinet qui paie, donc c'est lui qui décide de ce que son client
 * reçoit — décision de Rose du 24/08/2026. Le serveur ne l'accorde qu'au membre
 * du cabinet (`DOSSIER × MODIFIER` dans la matrice de C2) et le refuse à la
 * direction de l'entreprise elle-même.
 */
import axios from 'axios';
import { APP_CONFIG } from '@/constants/app';
import { tokenManager } from '@/utils/token-manager';

const api = axios.create({
  baseURL: APP_CONFIG.api.baseUrl,
  headers: { 'Content-Type': 'application/json' },
});
tokenManager.setupInterceptors(api);

/**
 * L'API enveloppe ses réponses dans `{ statusCode, data, meta }`.
 *
 * Ne pas la déballer rend l'enveloppe elle-même : `etat.sirh` vaut alors
 * `undefined`, et le premier `etat.sirh.actif` fait tomber tout l'écran sur
 * l'ErrorBoundary. C'est exactement ce qui est arrivé le 25/08/2026 — et la
 * capture l'a montré, là où le code compilait très bien.
 */
function extraire<T>(reponse: { data: unknown }): T {
  const corps = reponse.data as { data?: T };
  return (corps?.data ?? (corps as unknown)) as T;
}

/** Un état par défaut : rien d'ouvert. Jamais `undefined`. */
const RIEN_OUVERT: EtatDesModules = {
  sirh: { actif: false, fonctionnalites: [] },
  bdese: { actif: false },
};

/** Rend toujours une forme utilisable, quoi que réponde le serveur. */
function normaliser(brut: unknown): EtatDesModules {
  const e = brut as Partial<EtatDesModules> | undefined;
  return {
    sirh: {
      actif: !!e?.sirh?.actif,
      fonctionnalites: Array.isArray(e?.sirh?.fonctionnalites)
        ? e.sirh.fonctionnalites
        : [],
    },
    bdese: { actif: !!e?.bdese?.actif },
  };
}

/**
 * Les fonctionnalités du SIRH, dans les mots des utilisateurs.
 *
 * Six lignes, pas cinquante : c'est l'inventaire de ce qui est RÉELLEMENT
 * livré, repris de la cartographie du chantier C3 (9 capacités sur 53). Le
 * serveur refuse toute clé absente de cette liste — on ne peut pas facturer
 * quelque chose qui n'existe pas.
 */
export const FONCTIONNALITES_SIRH = [
  { valeur: 'conges', libelle: 'Congés et absences' },
  { valeur: 'espace_salarie', libelle: 'Espace du salarié' },
  { valeur: 'bureau_responsable', libelle: 'Bureau du responsable' },
  { valeur: 'variables_paie', libelle: 'Variables de paie du mois' },
  { valeur: 'entretiens', libelle: 'Entretiens professionnels' },
  { valeur: 'formation', libelle: 'Formation' },
] as const;

export type FonctionnaliteSirh =
  (typeof FONCTIONNALITES_SIRH)[number]['valeur'];

export interface EtatDesModules {
  sirh: { actif: boolean; fonctionnalites: FonctionnaliteSirh[] };
  bdese: { actif: boolean };
}

export const ETAT_PAR_DEFAUT = RIEN_OUVERT;

export const modulesDossier = {
  async lire(folderUuid: string): Promise<EtatDesModules> {
    return normaliser(
      extraire(await api.get(`/dossiers/${folderUuid}/modules`))
    );
  },

  async reglerSirh(
    folderUuid: string,
    actif: boolean,
    fonctionnalites: FonctionnaliteSirh[]
  ): Promise<EtatDesModules> {
    return normaliser(
      extraire(
        await api.put(`/dossiers/${folderUuid}/modules/sirh`, {
          actif,
          fonctionnalites,
        })
      )
    );
  },

  /** Aucune fonctionnalité à passer : la BDESE ne se découpe pas. */
  async reglerBdese(
    folderUuid: string,
    actif: boolean
  ): Promise<EtatDesModules> {
    return normaliser(
      extraire(
        await api.put(`/dossiers/${folderUuid}/modules/bdese`, { actif })
      )
    );
  },

  /**
   * La toute première clé, remise à la direction de l'entreprise.
   *
   * Ne fonctionne qu'une fois : ensuite c'est la direction qui invite les
   * siens, et le serveur refuse au cabinet de le faire à sa place.
   */
  async ouvrirLePremierAcces(
    folderUuid: string,
    email: string
  ): Promise<{ email: string; envoye: boolean }> {
    return extraire(
      await api.post(`/dossiers/${folderUuid}/premier-acces`, { email })
    );
  },
};
